import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Disc3, ChevronDown, ChevronUp, CheckCircle2, Clock, Users } from "lucide-react";
import { managersProject, managersDeleteDraft } from "@/lib/afmApi";
import { Loading } from "@/components/common";
import ReleaseReports from "@/components/ReleaseReports";
import { BucketTabs, StatusCell, bucketOf } from "@/components/ReleaseStatus";

const errText = (e) => e?.response?.data?.detail || "No se pudo completar";
const when = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleString("es-VE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Proyecto de UN artista de la subcuenta: borradores, lanzamientos (propios y colaboraciones),
// estados de envío, su acuerdo y regalías/métricas según los permisos de quien mira.
export default function ManagerProject({ artistId }) {
  const navigate = useNavigate();
  const [d, setD] = useState(undefined);
  const [bucket, setBucket] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setD(undefined);
    managersProject(artistId).then(setD).catch((e) => setD({ error: errText(e) }));
  }, [artistId]);
  useEffect(() => { load(); setBucket("all"); setOpen(null); }, [load]);

  if (d === undefined) return <Loading />;
  if (d.error) return <p className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500">{d.error}</p>;

  const p = d.perms || {};
  const a = d.artist;
  const rels = d.releases || [];
  const shown = bucket === "all" ? rels : rels.filter((r) => bucketOf(r) === bucket);
  // El artista no ve el bruto de la distribuidora: su parte sale en "Tus ganancias".
  const reportTabs = [p.accounting && d.role !== "artist" && "royalties", p.insights && "metrics"].filter(Boolean);
  const removeDraft = async (id) => {
    if (!window.confirm("¿Borrar este borrador?")) return;
    try { await managersDeleteDraft(id); toast.success("Borrador borrado"); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-6" data-testid="artist-project">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Proyecto del artista</div>
          <h2 className="font-serif text-2xl font-bold text-black">{a.name}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            {a.split?.artist_pct != null && <span>{d.role === "artist" ? `Tu parte: ${a.split.artist_pct}%` : `${a.split.artist_pct}% artista`}</span>}
            {a.split?.status === "signed"
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Acuerdo firmado</span>
              : a.split?.status !== "none" && <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3.5 w-3.5" /> Falta firmar el acuerdo</span>}
            <span>{rels.length} lanzamiento(s)</span>
          </div>
        </div>
        {p.catalog && (
          <button onClick={() => navigate(`/distribute?para=${encodeURIComponent(a.id)}`)} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white" data-testid="new-release-for-artist">
            <Plus className="h-4 w-4" /> Nuevo lanzamiento para {a.name}
          </button>
        )}
      </div>

      {p.catalog && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-black">Borradores</h3>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
            {(d.drafts || []).map((x) => (
              <div key={x.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                <Pencil className="h-4 w-4 text-slate-300" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-black">{x.title || "Sin título"}</div>
                  <div className="text-xs text-slate-500">Guardado {when(x.updated_at)}{x.updated_by ? ` · ${x.updated_by}` : ""}</div>
                </div>
                <button onClick={() => navigate(`/distribute?para=${encodeURIComponent(a.id)}&borrador=${encodeURIComponent(x.id)}`)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:border-black">Continuar</button>
                <button onClick={() => removeDraft(x.id)} className="text-slate-400 hover:text-red-500" title="Borrar"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            {!(d.drafts || []).length && <p className="px-5 py-5 text-center text-sm text-slate-400">No hay borradores. Se guardan solos mientras llenas un lanzamiento.</p>}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-black">Lanzamientos</h3>
        {rels.length > 0 && <BucketTabs rows={rels} value={bucket} onChange={setBucket} />}
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {shown.map((r) => {
            const canReports = r.distribution_state === "forwarded" && reportTabs.length > 0;
            return (
              <div key={r.release_id} className="px-5 py-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <Disc3 className="h-4 w-4 text-slate-300" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-black">{r.title || "Sin título"}</div>
                    <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                      {r.role_in === "colaboracion"
                        ? <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 font-semibold text-violet-700"><Users className="h-3 w-3" /> Colaboración en el lanzamiento de {r.with?.[0]}</span>
                        : r.with?.length ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> con {r.with.join(", ")}</span> : null}
                      {r.release_date && <span>{r.release_date}</span>}
                      {r.assigned && <span>· asignado por Always Fresh</span>}
                    </div>
                  </div>
                  <StatusCell r={r} />
                  {canReports && (
                    <button onClick={() => setOpen(open === r.release_id ? null : r.release_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-black">
                      {reportTabs.includes("royalties") ? "Regalías y métricas" : "Métricas"} {open === r.release_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
                {r.review_status === "needs_fix" && r.review_issues?.length > 0 && !r.distribution_state && (
                  <ul className="mt-2 list-disc space-y-0.5 rounded-lg bg-red-50 py-2 pl-8 pr-3 text-xs text-red-800">
                    {r.review_issues.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                )}
                {open === r.release_id && canReports && <div className="mt-3"><ReleaseReports releaseId={r.release_id} show={reportTabs} /></div>}
              </div>
            );
          })}
          {!shown.length && <p className="px-5 py-8 text-center text-sm text-slate-400">{rels.length ? "Nada en este grupo." : "Todavía no hay lanzamientos en este proyecto."}</p>}
        </div>
      </section>
    </div>
  );
}
